export const services = [
    {
        slug: "performance-marketing-strategy",
        title: "Performance Marketing Strategy",
        short: "Channels, funnels, KPIs and reporting wired into one measurable growth system.",
        long: "Strategy is not a deck. It is a decision system. I build the channel mix, the conversion funnel, the KPI tree, and the weekly reporting cadence — connected to revenue, not vanity metrics.",
        bullets: [
            "Acquisition cost & payback modelling",
            "Channel-funnel-CRM mapping",
            "Weekly KPI tree + executive reporting",
            "Test backlog with prioritised hypotheses",
        ],
    },
    {
        slug: "seo-organic-visibility",
        title: "SEO & Organic Visibility",
        short: "Technical foundations, content engines, and topical authority that compound.",
        long: "Lasting organic growth is built on three layers: technical health, intent-mapped content, and internal authority distribution. I work on all three at once.",
        bullets: [
            "Technical SEO + Core Web Vitals",
            "Keyword & intent mapping",
            "Editorial planning + content briefs",
            "Internal linking & topical clusters",
        ],
    },
    {
        slug: "ppc-paid-media",
        title: "Pay-Per-Click & Paid Media",
        short: "Google, Meta, LinkedIn and programmatic campaigns optimised for qualified pipeline.",
        long: "Most paid accounts have at least 30% of spend going to traffic that never converts. I rebuild structure, audiences and creative so every dollar earns its place.",
        bullets: [
            "Account restructure (campaigns, keywords, audiences)",
            "Creative testing framework",
            "Conversion tracking & offline import",
            "ROAS / CAC optimisation",
        ],
    },
    {
        slug: "social-media-marketing",
        title: "Social Media Marketing",
        short: "Platform-native content and paid social tuned to your audience and funnel stage.",
        long: "Social only earns budget when it produces measurable demand. I plan content and paid social around audience clusters, funnel stage and revenue events.",
        bullets: [
            "Content pillar strategy",
            "Paid social structure (Meta, LinkedIn, TikTok)",
            "Audience targeting & lookalike strategy",
            "Performance reporting",
        ],
    },
    {
        slug: "marketing-analytics-reporting",
        title: "Marketing Analytics & Reporting",
        short: "Clean tracking, honest dashboards, and the truth about what drives revenue.",
        long: "If tracking is broken, every other decision is a guess. I rebuild the measurement stack so leadership can finally trust the numbers.",
        bullets: [
            "GA4 + Tag Manager hardening",
            "Server-side tracking & CAPI",
            "Looker Studio executive dashboards",
            "Attribution & data quality audits",
        ],
    },
    {
        slug: "google-marketing-platform",
        title: "Google Marketing Platform",
        short: "Enterprise-grade media operations across SA360, DV360 and CM360.",
        long: "For larger brands, the Google Marketing Platform is the difference between fragmented buying and a single, programmatic system. I help teams operate it without drowning in complexity.",
        bullets: [
            "SA360 search bid strategies",
            "DV360 programmatic media",
            "CM360 cross-channel tracking",
            "Cross-channel performance review",
        ],
    },
];

export const aiSolutions = [
    {
        slug: "ai-marketing-automation",
        title: "AI Marketing Automation",
        tag: "Workflows",
        short: "Replace mid-funnel manual work with AI-driven workflows that route, enrich, and respond in real time.",
        long: "AI marketing automation works best in the repetitive, high-volume middle of the funnel: enrichment, scoring, routing, follow-up. I deploy pragmatic agent workflows that take pressure off your team while improving response speed and lead quality.",
        deliverables: [
            "Lead enrichment + scoring agents",
            "Routing to sales in under 60 seconds",
            "Automated nurture journeys",
            "QA + human-in-the-loop guardrails",
        ],
        outcomes: ["Faster lead response", "Higher SDR efficiency", "Cleaner CRM data"],
    },
    {
        slug: "ai-agents-for-business",
        title: "AI Agents for Business",
        tag: "Agents",
        short: "Custom AI agents that handle research, reporting, and customer-facing workflows.",
        long: "Agents are not chatbots. They are bounded systems that complete a task, with tools, memory and guardrails. I help businesses identify the right tasks to automate and ship working agents — not demos.",
        deliverables: [
            "Use-case discovery and ROI sizing",
            "Tool-using research and reporting agents",
            "Customer-facing assistants",
            "Evaluation framework + observability",
        ],
        outcomes: ["Reduced ops time", "Faster decision cycles", "Scalable knowledge work"],
    },
    {
        slug: "ai-seo-and-content-tools",
        title: "AI SEO & Content Tools",
        tag: "Content",
        short: "AI-augmented content production that respects E-E-A-T and search intent.",
        long: "AI does not replace writers. It removes the bottleneck. I build structured workflows that turn SERP analysis, internal research and brand voice into briefs and drafts your editorial team can finish in a fraction of the time.",
        deliverables: [
            "SERP + intent analysis automation",
            "Content brief generation",
            "Topical cluster planning",
            "Quality scoring + human review",
        ],
        outcomes: ["3-5x content velocity", "Stronger topical authority", "Consistent brand voice"],
    },
    {
        slug: "ai-reporting-and-insights",
        title: "AI Reporting & Insights",
        tag: "Analytics",
        short: "Daily, narrative-driven performance reports that explain the why, not just the what.",
        long: "Most dashboards show numbers. Almost none answer the next question. I deploy reporting agents that pull from your media platforms, GA4 and CRM, then write a daily executive summary your leadership team actually reads.",
        deliverables: [
            "Multi-source data ingestion",
            "Anomaly detection",
            "Narrative daily / weekly reports",
            "Slack / email delivery",
        ],
        outcomes: ["Faster decisions", "Less time in spreadsheets", "Aligned leadership"],
    },
    {
        slug: "ai-for-paid-media",
        title: "AI for Paid Media",
        tag: "Paid Media",
        short: "Smarter bidding, creative testing, and budget allocation powered by data and AI.",
        long: "Platform AI is powerful, but it only optimises what you tell it to. I structure feeds, signals and creative testing so platform algorithms — and external AI layers — work in your favour, not against you.",
        deliverables: [
            "Conversion signal architecture",
            "Creative testing automation",
            "Budget pacing & allocation models",
            "Performance Max / Advantage+ governance",
        ],
        outcomes: ["Lower CAC", "Higher ROAS", "Faster creative iteration"],
    },
    {
        slug: "ai-for-forex-and-regulated-industries",
        title: "AI for Forex & Regulated Industries",
        tag: "Regulated",
        short: "Compliant AI workflows for forex, fintech and other high-risk verticals.",
        long: "Regulated marketing has unique constraints: compliance, brand-safety, geo-restrictions and high-velocity lead flow. I build AI workflows that respect those constraints while improving lead quality and conversion to deposit.",
        deliverables: [
            "Compliant lead scoring",
            "Geo-routed nurture flows",
            "Broker / advisor assignment logic",
            "Audit-ready logging",
        ],
        outcomes: ["Higher conversion-to-deposit", "Lower compliance risk", "Scalable lead ops"],
    },
];

export const problems = [
    {
        title: "Low Online Visibility",
        text: "Your business is not appearing strongly in search or across digital channels where qualified buyers are looking.",
    },
    {
        title: "Poor Conversion Rates",
        text: "You have traffic. You do not have leads, calls, demos or revenue events to match.",
    },
    {
        title: "Hard to Measure ROI",
        text: "Marketing spend is happening, but no one can confidently say what is driving pipeline.",
    },
    {
        title: "Ineffective Social Media",
        text: "Posts are going out, but engagement, traffic, leads and brand recall are all flat.",
    },
    {
        title: "Lack of Brand Awareness",
        text: "Your target audience does not recognise your name, value proposition or category position.",
    },
    {
        title: "Reputation & Trust Issues",
        text: "Reviews, social proof, messaging and credibility signals are not pulling their weight.",
    },
];

export const principles = [
    {
        kpi: "16+",
        label: "Years in Digital Marketing",
        text: "Hands-on across SEO, paid media, social, analytics and CRM — at agency and brand side.",
    },
    {
        kpi: "100%",
        label: "Performance Mindset",
        text: "Every campaign tied to acquisition cost, conversion rate, revenue contribution and ROI.",
    },
    {
        kpi: "F2L",
        label: "Full-Funnel Lens",
        text: "From first touch to lead capture, CRM nurture, conversion and post-sale reporting.",
    },
    {
        kpi: "REG",
        label: "Regulated Industries",
        text: "Forex, fintech, brokerage and other compliance-sensitive verticals.",
    },
];


// Case studies — anonymised under NDA. Numbers are representative ranges
// from real engagements (broker, fintech, SaaS, e-commerce verticals).
export const caseStudies = [
    {
        slug: "forex-broker-mena",
        sector: "Forex Broker",
        geo: "MENA · EU · LATAM",
        period: "9 months",
        headline: "Cut cost-per-FTD by 41% while doubling funded traders.",
        challenge:
            "A regulated multi-licence broker was scaling spend on Meta + Google but FTD cost climbed 70% YoY as accounts saturated. KYC-to-FTD conversion was stuck at 22%.",
        story: [
            "The broker had spent four years building a brand across MENA and LATAM, but growth had plateaued. Acquisition was running on autopilot — high-volume Meta + Search campaigns optimised for lead, not for funded trader. Compliance constraints limited messaging, and post-iOS14 signal loss was making Lookalikes unreliable.",
            "I rebuilt the acquisition system around intent stage and funnel economics. Campaigns were re-clustered by buyer readiness — research, comparison, ready-to-fund — and each cluster got its own bid strategy, creative angle and landing variant. Server-side tracking and Conversion API restored the signal Meta needed to optimise efficiently again.",
            "The CRM became the second engine. Welcome, KYC nudge, FTD-ready and dormant flows ran in parallel — by GEO and compliance status — over WhatsApp, email and SMS. Within 90 days we were seeing the cost-per-FTD curve bend; by month nine it sat 41% below the starting baseline at 2.4× the volume.",
        ],
        approach: [
            "Rebuilt campaign structure around intent (research vs. ready-to-fund)",
            "Server-side tracking + conversion API for CAPI signal restoration",
            "Lifecycle email + SMS sequences mapped to KYC stage",
            "GEO-specific creative angles, compliance-reviewed",
            "Funnel attribution model with revenue-weighted touchpoints",
            "Weekly creative testing matrix — 18 angles per quarter",
        ],
        metrics: [
            { label: "Cost per FTD", value: "−41%", trend: "down" },
            { label: "Funded traders", value: "+108%", trend: "up" },
            { label: "KYC → FTD", value: "22% → 38%", trend: "up" },
            { label: "Blended ROAS", value: "3.1× → 5.4×", trend: "up" },
        ],
        chart: [42, 44, 41, 38, 36, 33, 30, 28, 27, 25, 24, 23],
        chartLabel: "Cost per FTD (indexed, monthly)",
        keywords:
            "forex marketing case study, broker FTD optimization, KYC to FTD conversion, regulated paid media MENA, conversion API forex, walid cheikh case study",
    },
    {
        slug: "fintech-b2b-saas",
        sector: "B2B Fintech SaaS",
        geo: "GCC · UK",
        period: "6 months",
        headline: "Built a $12M qualified pipeline from zero outbound demand.",
        challenge:
            "Series-A fintech with strong product but flat inbound. Sales relied on founder network. No measurable demand engine, attribution was dark.",
        story: [
            "The founders had product-market fit but no repeatable acquisition motion. Marketing was a part-time effort. Every closed deal traced back to the founder's LinkedIn DMs. The board wanted a $10M+ pipeline by mid-year — without doubling headcount.",
            "I designed a tight ABM + content + paid retargeting loop. We started with a 480-account target list segmented by ICP fit and timing signals (6sense intent + funding triggers). Founder-led content — three pillar pieces a month — was the centre of gravity, amplified into the target list via LinkedIn Ads.",
            "Attribution was rebuilt in HubSpot + GA4 with multi-touch revenue weighting so sales and marketing finally agreed on what was working. A clear MQL → SQL SLA closed the handoff gap. Six months in, the pipeline crossed $12.4M with sales cycle compressed by 27% and CAC payback down to nine months.",
        ],
        approach: [
            "ABM list × LinkedIn Ads × intent retargeting via 6sense signals",
            "GA4 + HubSpot attribution model with multi-touch revenue weighting",
            "Founder-led content engine — 3 pillar pieces/month with paid amplification",
            "Sales-marketing SLA + MQL→SQL handoff playbook",
            "Custom dashboards: pipeline by source, velocity, conversion stage",
            "Event marketing programme — 4 invite-only roundtables in Dubai + London",
        ],
        metrics: [
            { label: "Qualified pipeline", value: "$12.4M", trend: "up" },
            { label: "MQL → SQL", value: "+62%", trend: "up" },
            { label: "Sales cycle", value: "−27%", trend: "down" },
            { label: "CAC payback", value: "14 → 9 mo", trend: "down" },
        ],
        chart: [12, 18, 26, 35, 42, 51, 63, 74, 82, 90, 96, 100],
        chartLabel: "Cumulative qualified pipeline (indexed)",
        keywords:
            "B2B fintech marketing case study, ABM campaign case study Dubai, fintech demand generation, HubSpot attribution case study, SaaS pipeline growth, walid cheikh fintech",
    },
    {
        slug: "ecommerce-dtc",
        sector: "DTC E-commerce",
        geo: "UAE · KSA · UK",
        period: "12 months",
        headline: "Scaled paid revenue 4.2× while holding blended CAC flat.",
        challenge:
            "Premium DTC brand hit a ceiling on Meta after iOS14. Repeat-purchase data was siloed; creative testing was ad-hoc.",
        story: [
            "A profitable but plateaued DTC brand was running 80% of paid spend on Meta. After iOS14 the cost-per-purchase climbed and Lookalikes degraded. They couldn't see customer LTV in any one place — Shopify, Klaviyo and Meta each had a partial view.",
            "I unified the data layer first: Klaviyo + Shopify + GA4 streaming into a single attribution view, with customer LTV bucketed by acquisition source. Then we restructured paid: TikTok and Pinterest were reactivated as discovery channels at lower CPMs, Meta was rebuilt around interest + behaviour clusters instead of Lookalikes.",
            "Creative became the leverage point — a weekly testing matrix across UGC, founder POV and product demo formats. Post-purchase Klaviyo flows (review request, replenish, win-back by LTV segment) lifted the repeat rate from 23% to 41%. Twelve months in, paid revenue was 4.2× the baseline with blended CAC flat.",
        ],
        approach: [
            "Klaviyo + Shopify + GA4 unified into a single attribution layer",
            "Weekly creative testing matrix (UGC, founder POV, product demo)",
            "Post-purchase flows + win-back automation by LTV segment",
            "TikTok + Pinterest reactivated as discovery channels",
            "Interest + behaviour-based Meta clusters (Lookalike-free)",
            "Margin-based ROAS targeting instead of revenue-based",
        ],
        metrics: [
            { label: "Revenue", value: "4.2×", trend: "up" },
            { label: "Blended CAC", value: "−3%", trend: "flat" },
            { label: "Repeat rate", value: "23% → 41%", trend: "up" },
            { label: "Avg order value", value: "+18%", trend: "up" },
        ],
        chart: [22, 24, 28, 33, 41, 48, 56, 64, 72, 80, 88, 92],
        chartLabel: "Monthly paid revenue (indexed)",
        keywords:
            "DTC ecommerce marketing case study, Shopify Klaviyo case study, post-iOS14 paid media scaling, ecommerce CAC optimization, Meta TikTok ecommerce strategy",
    },
    {
        slug: "regulated-leadgen-dubai",
        sector: "Regulated Lead Gen",
        geo: "UAE",
        period: "5 months",
        headline: "Tripled qualified leads at 38% lower cost with AI-assisted ops.",
        challenge:
            "High-ticket consultative service in a regulated category. Form fills were noisy — 60% disqualified before sales call.",
        story: [
            "A high-ticket consulting practice in a regulated category was drowning in unqualified form fills. Sales spent half their week chasing tyre-kickers. Paid channels looked efficient on a cost-per-lead basis but the cost-per-qualified-lead was 4× worse.",
            "I built an AI pre-qualification agent into the landing page — a short conversational triage on intent, budget and timing — that routed only high-fit leads into the CRM. Everything else got a self-serve resource path. Server-side GTM and offline conversion uploads then taught Google Ads which leads were actually qualified.",
            "WhatsApp Business automation handled the SLA on hot leads (median first-touch 4 minutes). Creative was re-tested every fortnight in a structured matrix. Five months in, qualified leads were up 3.1× at a 38% lower cost — and sales finally had clean pipeline to work with.",
        ],
        approach: [
            "Pre-qualification AI agent on landing page (intent + budget triage)",
            "Server-side GTM + offline conversion uploads to Google Ads",
            "WhatsApp Business automation for instant SLA on hot leads",
            "Creative angles re-tested every 14 days with structured matrix",
            "Custom CRM scoring: behavioural + firmographic + conversational",
            "Sales playbook rebuilt around qualified-lead signals",
        ],
        metrics: [
            { label: "Qualified leads", value: "3.1×", trend: "up" },
            { label: "Cost per qualified", value: "−38%", trend: "down" },
            { label: "Lead → call", value: "11% → 27%", trend: "up" },
            { label: "Spam / disqualified", value: "60% → 14%", trend: "down" },
        ],
        chart: [40, 42, 38, 34, 30, 27, 24, 21, 19, 17, 15, 14],
        chartLabel: "Cost per qualified lead (indexed)",
        keywords:
            "AI lead qualification case study, regulated lead gen Dubai, WhatsApp marketing automation, server-side GTM Google Ads, qualified lead cost reduction, walid cheikh AI marketing",
    },
];

// Tools / platforms Walid works in — used for the trust strip.
// These are platforms (skills proof), not client logos.
export const platformStack = [
    "Google Ads",
    "Meta Business",
    "LinkedIn Ads",
    "TikTok Ads",
    "Programmatic DV360",
    "GA4",
    "Google Tag Manager",
    "Server-side Tracking",
    "HubSpot",
    "Salesforce",
    "Klaviyo",
    "Segment",
    "Mixpanel",
    "Looker Studio",
    "Hotjar",
    "Zapier",
    "n8n",
    "OpenAI",
    "Anthropic Claude",
    "Make",
];
